import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import CowDetailPage from './CowDetailPage';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS, getImageUrl, getApiUrl } from '../config/api';

const CowInvestmentDetail = ({ investasiId, onBack, activeBottomTab, setActiveBottomTab }) => {
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCowDetail, setShowCowDetail] = useState(false);
  const [selectedCow, setSelectedCow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState({
    totalCows: '0 Ekor',
    soldCows: '0 Ekor',
    projectedProfit: 'Rp 0',
    totalInvestasi: 'Rp 0'
  });
  const [investmentData, setInvestmentData] = useState({
    investmentCode: '-',
    investmentAmount: 'Rp 0',
    bank: '-',
    investmentDate: '-',
    description: '-'
  });
  const [cowsData, setCowsData] = useState([]);
  const [pembayaranData, setPembayaranData] = useState(null);
  const [pembayaranImages, setPembayaranImages] = useState([]);
  const [loadingPembayaran, setLoadingPembayaran] = useState(false);
  const [dokumenFiles, setDokumenFiles] = useState([]);
  const [loadingDokumen, setLoadingDokumen] = useState(false);
  const [dokumenDetail, setDokumenDetail] = useState(null);
  const [userData, setUserData] = useState(null);

  // Format currency
  const formatCurrency = (value) => {
    // Convert string to number if needed
    let numValue = value;
    if (typeof value === 'string') {
      // Remove any non-numeric characters except dots and commas
      numValue = value.replace(/[^\d.,]/g, '');
      // Replace comma with dot for decimal separator
      numValue = numValue.replace(',', '.');
      numValue = parseFloat(numValue) || 0;
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(numValue || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    } catch (error) {
      return dateString;
    }
  };

  // Fetch data investasi dari API
  useEffect(() => {
    const fetchInvestasiDetail = async () => {
      if (!investasiId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // 1. Fetch data investasi dari /api/investasi/{investasi_id}
        const investasiResponse = await apiClient.get(API_ENDPOINTS.INVESTASI.DETAIL(investasiId));
        
        let investasi = null;
        if (investasiResponse.data && investasiResponse.data.investasi) {
          investasi = investasiResponse.data.investasi;
        } else if (investasiResponse.data && !investasiResponse.data.investasi) {
          investasi = investasiResponse.data;
        } else if (!investasiResponse.data) {
          investasi = investasiResponse;
        }

        if (!investasi) {
          throw new Error('Data investasi tidak ditemukan');
        }

        // Fetch user data dari investasi.user_id
        const userId = investasi.user_id || investasi.user?.id;
        if (userId) {
          try {
            const userResponse = await apiClient.get(API_ENDPOINTS.USER.GET_BY_ID(userId));
            let user = null;
            if (userResponse.data && userResponse.data.user) {
              user = userResponse.data.user;
            } else if (userResponse.data && userResponse.data.data) {
              user = userResponse.data.data;
            } else if (userResponse.data) {
              user = userResponse.data;
            } else {
              user = userResponse;
            }
            setUserData(user);
          } catch (userError) {
            console.error('Error fetching user data:', userError);
            // Fallback: ambil dari localStorage
            const savedUserData = localStorage.getItem('user_data');
            if (savedUserData) {
              setUserData(JSON.parse(savedUserData));
            }
          }
        }

        // 2. Fetch investasi-produk dari /api/investasi-produk/investasi/{investasi_id}
        const investasiProdukResponse = await apiClient.get(API_ENDPOINTS.INVESTASI_PRODUK.BY_INVESTASI(investasiId));
        
        let investasiProdukList = [];
        if (investasiProdukResponse.data && investasiProdukResponse.data.investasi_produks && Array.isArray(investasiProdukResponse.data.investasi_produks)) {
          investasiProdukList = investasiProdukResponse.data.investasi_produks;
        } else if (investasiProdukResponse.data && Array.isArray(investasiProdukResponse.data)) {
          investasiProdukList = investasiProdukResponse.data;
        } else if (Array.isArray(investasiProdukResponse)) {
          investasiProdukList = investasiProdukResponse;
        }

        // Filter hanya yang tidak soft deleted
        const investasiProdukAktif = investasiProdukList.filter(ip => ip.deleted_at === null);

        // 3. Fetch produk detail untuk setiap investasi-produk
        const produkIds = investasiProdukAktif
          .map(ip => ip.produk_id)
          .filter(id => id !== null && id !== undefined);

        let produkMap = {};
        let totalInvestasi = 0;
        let soldCowsCount = 0;

        if (produkIds.length > 0) {
          const uniqueProdukIds = [...new Set(produkIds)];
          const produkPromises = uniqueProdukIds.map(async (produkId) => {
            try {
              const produkResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_DETAIL(produkId));
              
              let produk = null;
              if (produkResponse.data && produkResponse.data.produk) {
                produk = produkResponse.data.produk;
              } else if (produkResponse.data && !produkResponse.data.produk) {
                produk = produkResponse.data;
              } else if (!produkResponse.data) {
                produk = produkResponse;
              }

              return { produkId, produk };
            } catch (error) {
              console.error(`Error fetching produk ${produkId}:`, error);
              return { produkId, produk: null };
            }
          });

          const produkResults = await Promise.all(produkPromises);
          produkResults.forEach(item => {
            if (item.produk) {
              produkMap[item.produkId] = item.produk;
            }
          });

          // Hitung total investasi dan sapi terjual dari investasi-produk
          investasiProdukAktif.forEach(ip => {
            if (ip.produk_id && produkMap[ip.produk_id]) {
              const produk = produkMap[ip.produk_id];
              const hargaBeli = produk.harga_beli || produk.price || produk.harga || 0;
              totalInvestasi += typeof hargaBeli === 'number' ? hargaBeli : parseFloat(hargaBeli) || 0;
              
              // Hitung sapi terjual
              if (produk.is_sold === true) {
                soldCowsCount++;
              }
            }
          });
        }

        // Hitung jumlah sapi
        const totalCowsCount = investasiProdukAktif.length;

        // Update summary data
        setSummaryData({
          totalCows: `${totalCowsCount} Ekor`,
          soldCows: `${soldCowsCount} Ekor`,
          projectedProfit: 'Rp 0', // Hardcode seperti diminta
          totalInvestasi: formatCurrency(totalInvestasi) // Total dari penjumlahan harga beli
        });

        // Update investment data
        const bankName = investasi.bank || investasi.rekening || investasi.bank_name || 'BCA';
        const bankDisplay = bankName.includes('BCA') ? 'BCA' : 
                          bankName.includes('BNI') ? 'BNI' :
                          bankName.includes('BRI') ? 'BRI' :
                          bankName.includes('Mandiri') ? 'Mandiri' :
                          bankName;

        setInvestmentData({
          investmentCode: investasi.id || investasi.investasi_id || investasi.kode || '-',
          investmentAmount: formatCurrency(totalInvestasi),
          bank: bankDisplay,
          investmentDate: formatDate(investasi.created_at || investasi.tanggal || investasi.date),
          description: investasi.deskripsi || '-'
        });

        // Transform investasi-produk menjadi cowsData
        const transformedCowsData = await Promise.all(
          investasiProdukAktif.map(async (ip) => {
            const produk = produkMap[ip.produk_id];
            if (!produk) {
              return null;
            }

            // Fetch image untuk produk
            let imageUrl = '/sapi1.jpg';
            try {
              const imagesResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_IMAGES(produk.id));
              if (imagesResponse.data && imagesResponse.data.images && Array.isArray(imagesResponse.data.images) && imagesResponse.data.images.length > 0) {
                const firstImage = imagesResponse.data.images[0];
                if (firstImage.url) {
                  imageUrl = getImageUrl(firstImage.url);
                }
              }
            } catch (imageError) {
              console.warn(`Error fetching image for produk ${produk.id}:`, imageError);
            }

            const hargaBeli = produk.harga_beli || produk.price || produk.harga || 0;
            const hargaJual = produk.harga_jual || produk.selling_price || 0;
            const feeMarketing = produk.fee_marketing || 0;

            return {
              id: produk.id || ip.produk_id,
              breed: produk.jenis_produk_detail_nama || produk.jenis_produk_nama || produk.nama || 'Sapi',
              code: `${produk.id || ip.produk_id} ${new Date().getFullYear()} / ${formatDate(investasi.created_at)}`,
              productType: produk.jenis_produk_detail_nama || produk.jenis_produk_nama || produk.nama || '',
              targetWeight: produk.target_bobot ? `${produk.target_bobot} Kg` : '-',
              category: produk.kategori_produk_nama || produk.kategori || 'Standard',
              purchasePrice: formatCurrency(hargaBeli),
              sellingPrice: formatCurrency(hargaJual),
              marketingFee: formatCurrency(feeMarketing),
              profitBersama: formatCurrency(0), // TODO: Hitung dari API jika ada
              profit30: formatCurrency(0), // TODO: Hitung dari API jika ada
              status: produk.is_sold === true ? 'sudah_terjual' : 'belum_terjual',
              isSold: produk.is_sold || false,
              image: imageUrl,
              rawProdukData: produk
            };
          })
        );

        // Filter out null values
        setCowsData(transformedCowsData.filter(cow => cow !== null));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching investasi detail:', error);
        setLoading(false);
      }
    };

    fetchInvestasiDetail();
  }, [investasiId]);

  // Fetch data pembayaran ketika modal dibuka
  useEffect(() => {
    const fetchPembayaranData = async () => {
      if (!showTransferModal || !investasiId) {
        return;
      }

      try {
        setLoadingPembayaran(true);

        // Fetch pembayaran dari /api/pembayaran/by-investasi/{investasi_id}
        const pembayaranResponse = await apiClient.get(API_ENDPOINTS.PEMBAYARAN.BY_INVESTASI(investasiId));
        
        let pembayaranList = [];
        if (pembayaranResponse.data && pembayaranResponse.data.pembayarans && Array.isArray(pembayaranResponse.data.pembayarans)) {
          pembayaranList = pembayaranResponse.data.pembayarans;
        } else if (pembayaranResponse.data && Array.isArray(pembayaranResponse.data)) {
          pembayaranList = pembayaranResponse.data;
        } else if (Array.isArray(pembayaranResponse)) {
          pembayaranList = pembayaranResponse;
        }

        // Ambil pembayaran pertama (atau yang terbaru) jika ada
        if (pembayaranList.length > 0) {
          // Sort by created_at descending untuk ambil yang terbaru
          const sortedPembayaran = pembayaranList.sort((a, b) => {
            const dateA = new Date(a.created_at || a.tanggal || 0);
            const dateB = new Date(b.created_at || b.tanggal || 0);
            return dateB - dateA;
          });
          
          const latestPembayaran = sortedPembayaran[0];
          setPembayaranData(latestPembayaran);

          // Fetch images pembayaran dari /api/pembayaran/{pembayaran_id}/images
          // Gunakan ID pembayaran, bukan ID investasi
          const pembayaranId = latestPembayaran.id || latestPembayaran.pembayaran_id;
          if (pembayaranId) {
            try {
              const imagesResponse = await apiClient.get(API_ENDPOINTS.PEMBAYARAN.IMAGES(pembayaranId));
              let images = [];
              if (imagesResponse.data && imagesResponse.data.images && Array.isArray(imagesResponse.data.images)) {
                images = imagesResponse.data.images;
              } else if (imagesResponse.data && Array.isArray(imagesResponse.data)) {
                images = imagesResponse.data;
              } else if (Array.isArray(imagesResponse)) {
                images = imagesResponse;
              }
              setPembayaranImages(images);
            } catch (imageError) {
              console.error('Error fetching pembayaran images:', imageError);
              setPembayaranImages([]);
            }
          } else {
            setPembayaranImages([]);
          }
        } else {
          setPembayaranData(null);
          setPembayaranImages([]);
        }
      } catch (error) {
        console.error('Error fetching pembayaran data:', error);
        setPembayaranData(null);
        setPembayaranImages([]);
      } finally {
        setLoadingPembayaran(false);
      }
    };

    fetchPembayaranData();
  }, [showTransferModal, investasiId]);

  // Fetch dokumen files dan detail
  useEffect(() => {
    const fetchDokumenData = async () => {
      if (!investasiId) {
        return;
      }

      try {
        setLoadingDokumen(true);

        // 1. Fetch dokumen files dari /api/dokumen/{investasi_id}/files
        const filesResponse = await apiClient.get(API_ENDPOINTS.DOKUMEN.FILES(investasiId));
        
        let files = [];
        // Handle struktur response: data.documents atau data.data.documents atau langsung array
        if (filesResponse.data && filesResponse.data.documents && Array.isArray(filesResponse.data.documents)) {
          files = filesResponse.data.documents;
        } else if (filesResponse.data && filesResponse.data.data && filesResponse.data.data.documents && Array.isArray(filesResponse.data.data.documents)) {
          files = filesResponse.data.data.documents;
        } else if (filesResponse.data && filesResponse.data.files && Array.isArray(filesResponse.data.files)) {
          files = filesResponse.data.files;
        } else if (filesResponse.data && Array.isArray(filesResponse.data)) {
          files = filesResponse.data;
        } else if (Array.isArray(filesResponse)) {
          files = filesResponse;
        }

        setDokumenFiles(files);

        // 2. Fetch dokumen detail dari /api/dokumen?investasi_id={investasi_id} untuk mendapatkan deskripsi
        try {
          const dokumenResponse = await apiClient.get(API_ENDPOINTS.DOKUMEN.BY_INVESTASI(investasiId));
          
          let dokumenList = [];
          if (dokumenResponse.data && dokumenResponse.data.dokumen && Array.isArray(dokumenResponse.data.dokumen)) {
            dokumenList = dokumenResponse.data.dokumen;
          } else if (dokumenResponse.data && dokumenResponse.data.data && Array.isArray(dokumenResponse.data.data)) {
            dokumenList = dokumenResponse.data.data;
          } else if (dokumenResponse.data && Array.isArray(dokumenResponse.data)) {
            dokumenList = dokumenResponse.data;
          } else if (Array.isArray(dokumenResponse)) {
            dokumenList = dokumenResponse;
          }

          // Ambil dokumen pertama jika ada
          if (dokumenList.length > 0) {
            setDokumenDetail(dokumenList[0]);
          }
        } catch (dokumenError) {
          console.error('Error fetching dokumen detail:', dokumenError);
        }

      } catch (error) {
        console.error('Error fetching dokumen files:', error);
        setDokumenFiles([]);
      } finally {
        setLoadingDokumen(false);
      }
    };

    fetchDokumenData();
  }, [investasiId]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 KB';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Get file type from filename
  const getFileType = (filename) => {
    if (!filename) return 'Document';
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return 'PDF Document';
    if (['doc', 'docx'].includes(ext)) return 'Word Document';
    if (['xls', 'xlsx'].includes(ext)) return 'Excel Document';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'Image';
    return 'Document';
  };

  // Format date time
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: '-', time: '-' };
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return {
        date: `${day}/${month}/${year}`,
        time: `${hours}:${minutes}:${seconds}`
      };
    } catch (error) {
      return { date: dateString, time: '-' };
    }
  };

  // Jika sedang menampilkan detail sapi
  if (showCowDetail && selectedCow) {
    return (
      <CowDetailPage 
        cowData={selectedCow}
        onBack={() => setShowCowDetail(false)}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Wrapper */}
      <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header */}
        <header className="px-4 pt-8 pb-4 bg-white border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button 
                onClick={onBack}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-primary text-xl"></i>
              </button>
              <div className="flex items-center space-x-2">
                <Logo size="small" showText={false} />
                <span className="font-bold text-primary text-lg">Detail Investasi</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-24 bg-gray-50">
          {loading ? (
            <div className="px-4 pt-6 space-y-4">
              {/* Loading Skeleton */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-lg p-4 text-center animate-pulse">
                    <div className="bg-gray-200 p-2 rounded-lg w-fit mx-auto mb-2 h-10 w-10"></div>
                    <div className="bg-gray-200 h-3 w-16 mx-auto mb-2 rounded"></div>
                    <div className="bg-gray-200 h-5 w-12 mx-auto rounded"></div>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-4 animate-pulse">
                <div className="bg-gray-200 h-6 w-32 mb-4 rounded"></div>
                <div className="space-y-3">
                  <div className="bg-gray-200 h-4 w-full rounded"></div>
                  <div className="bg-gray-200 h-4 w-full rounded"></div>
                  <div className="bg-gray-200 h-4 w-full rounded"></div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <section className="px-4 pt-6">
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                    <div className="bg-secondary p-2 rounded-lg w-fit mx-auto mb-2">
                      <i className="fa-solid fa-cow text-accent text-lg"></i>
                    </div>
                    <h4 className="text-gray-600 text-xs font-medium">Jumlah Sapi</h4>
                    <p className="text-lg font-bold text-primary mt-1">{summaryData.totalCows}</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                    <div className="bg-secondary p-2 rounded-lg w-fit mx-auto mb-2">
                      <i className="fa-solid fa-chart-line text-accent text-lg"></i>
                    </div>
                    <h4 className="text-gray-600 text-xs font-medium">Sapi Terjual</h4>
                    <p className="text-lg font-bold text-primary mt-1">{summaryData.soldCows}</p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-lg p-4 text-center">
                    <div className="bg-secondary p-2 rounded-lg w-fit mx-auto mb-2">
                      <i className="fa-solid fa-dollar-sign text-accent text-lg"></i>
                    </div>
                    <h4 className="text-gray-600 text-xs font-medium">Proyeksi Keuntungan</h4>
                    <p className="text-lg font-bold text-primary mt-1">{summaryData.projectedProfit}</p>
                  </div>
                </div>
              </section>

              {/* Transfer Proof Button */}
              <section className="px-4 mb-6">
                <button 
                  onClick={() => setShowTransferModal(true)}
                  className="w-full bg-accent text-primary font-semibold py-3 px-4 rounded-xl flex items-center justify-center space-x-2 hover:bg-accent/90 transition-colors"
                >
                  <i className="fa-solid fa-receipt"></i>
                  <span>Lihat Bukti Transfer</span>
                </button>
              </section>

              {/* Data Section */}
              <section className="px-4 mb-6">
                <div className="bg-white rounded-2xl shadow-lg p-4">
                  <h3 className="text-gray-700 font-semibold mb-3">Data</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Kode Investasi</span>
                      <span className="text-primary font-semibold text-sm">{investmentData.investmentCode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Jumlah Investasi</span>
                      <span className="text-primary font-semibold text-sm">{investmentData.investmentAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Ke Rekening</span>
                      <span className="text-primary font-semibold text-sm">{investmentData.bank}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Tanggal Investasi</span>
                      <span className="text-primary font-semibold text-sm">{investmentData.investmentDate}</span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Deskripsi Section */}
              <section className="px-4 mb-6">
                <div className="bg-white rounded-2xl shadow-lg p-4">
                  <h3 className="text-gray-700 font-semibold mb-3">Deskripsi</h3>
                  <p className="text-gray-600 text-sm">{investmentData.description}</p>
                </div>
              </section>

              {/* Data Sapi Investasi */}
              <section className="px-4 mb-6">
                <h3 className="text-gray-700 font-semibold mb-4">Data Sapi Investasi</h3>
                
                {cowsData.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                    <i className="fa-solid fa-cow text-4xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500">Belum ada data sapi investasi</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cowsData.map((cow, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  {/* Cow Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={cow.image} 
                      alt={`Sapi Investasi ${cow.id}`} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1">
                        <span className="text-primary font-bold text-lg">{cow.id}</span>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1">
                        <span className="text-primary font-semibold text-sm">{cow.breed}</span>
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-black/50 backdrop-blur-sm rounded-lg p-3">
                        <div className="flex items-center justify-between text-white">
                          <div>
                            <p className="text-sm font-semibold">#IngatQurban</p>
                            <p className="text-xs opacity-80">#IngatPakMoo</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs opacity-80">pakMoo</p>
                            <p className="text-xs opacity-80">HUSA SENTOSA FARM</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cow Details */}
                  <div className="p-4">
                    <div className="text-center mb-4">
                      <h4 className="text-primary font-bold text-lg">{cow.breed}</h4>
                      <p className="text-gray-600 text-sm">{cow.code}</p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Jenis Produk</span>
                        <span className="text-primary font-semibold text-sm">{cow.productType}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Target Bobot</span>
                        <span className="text-primary font-semibold text-sm">{cow.targetWeight}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Kategori</span>
                        <span className="text-primary font-semibold text-sm">{cow.category}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Harga Beli</span>
                        <span className="text-primary font-semibold text-sm">{cow.purchasePrice}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Harga Jual</span>
                        <span className="text-primary font-semibold text-sm">{cow.sellingPrice}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Fee Marketing</span>
                        <span className="text-primary font-semibold text-sm">{cow.marketingFee}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Profit Bersama</span>
                        <span className="text-green-600 font-semibold text-sm">{cow.profitBersama}</span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 text-sm">Profit 30%</span>
                        <span className="text-green-600 font-semibold text-sm">{cow.profit30}</span>
                      </div>
                    </div>
                  </div>

                      {/* Action Buttons */}
                      <div className="px-4 pb-4 space-y-2">
                        {cow.status === 'sudah_terjual' ? (
                          <button className="w-full bg-green-500 text-white font-semibold py-3 px-4 rounded-xl">
                            Sudah Terjual
                          </button>
                        ) : (
                          <button className="w-full bg-gray-300 text-gray-600 font-semibold py-3 px-4 rounded-xl">
                            Belum Terjual
                          </button>
                        )}
                        
                        <button 
                          onClick={() => {
                            setSelectedCow(cow);
                            setShowCowDetail(true);
                          }}
                          className="w-full bg-accent text-primary font-semibold py-3 px-4 rounded-xl flex items-center justify-center space-x-2 hover:bg-accent/90 transition-colors"
                        >
                          <i className="fa-solid fa-info-circle"></i>
                          <span>Detail Sapi</span>
                        </button>
                      </div>
                    </div>
                  ))}
                  </div>
                )}
              </section>
            </>
          )}

          {/* Dokumen Perjanjian */}
          <section className="px-4">
            <h3 className="text-gray-700 font-semibold mb-4">Dokumen Perjanjian</h3>
            
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* File Dokumen Section */}
              <div className="bg-accent p-3">
                <h4 className="text-primary font-bold text-center text-white">File Dokumen</h4>
              </div>
              <div className="p-4">
                {loadingDokumen ? (
                  <div className="space-y-3 animate-pulse">
                    {[1, 2].map((i) => (
                      <div key={i} className="bg-white rounded-xl shadow p-4 flex items-center space-x-4">
                        <div className="bg-red-200 w-16 h-16 rounded-lg"></div>
                        <div className="flex-1 space-y-2">
                          <div className="bg-gray-200 h-4 w-48 rounded"></div>
                          <div className="bg-gray-200 h-3 w-24 rounded"></div>
                          <div className="bg-gray-200 h-3 w-32 rounded"></div>
                        </div>
                        <div className="bg-gray-200 w-24 h-10 rounded-lg"></div>
                      </div>
                    ))}
                  </div>
                ) : dokumenFiles.length > 0 ? (
                  <div className="space-y-3">
                    {dokumenFiles.map((file, index) => {
                      const fileName = typeof file === 'string' ? file : (file.filename || file.name || `File ${index + 1}`);
                      const fileSize = typeof file === 'object' ? (file.file_size || file.size || 0) : 0;
                      
                      // Build file URL - bisa dari file.url atau construct dari filename
                      let fullFileUrl = '';
                      if (typeof file === 'object' && file.url) {
                        fullFileUrl = file.url.startsWith('http') ? file.url : getApiUrl(file.url);
                      } else {
                        // Construct URL: /documents/dokumen/{investasi_id}/{filename}
                        const filePath = `/documents/dokumen/${investasiId}/${fileName}`;
                        fullFileUrl = getApiUrl(filePath);
                      }

                      return (
                        <div key={index} className="bg-white rounded-xl shadow p-4 flex items-center space-x-4">
                          {/* PDF Icon */}
                          <div className="bg-red-600 rounded-lg w-16 h-16 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-sm">PDF</span>
                          </div>

                          {/* File Details */}
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-800 font-semibold text-sm truncate">{fileName}</p>
                            <p className="text-gray-500 text-xs mt-1">{formatFileSize(fileSize)}</p>
                            <p className="text-gray-500 text-xs">{getFileType(fileName)}</p>
                          </div>

                          {/* Download Button */}
                          <button
                            onClick={() => {
                              if (fullFileUrl) {
                                const link = document.createElement('a');
                                link.href = fullFileUrl;
                                link.download = fileName;
                                link.target = '_blank';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              } else {
                                alert('URL file tidak tersedia');
                              }
                            }}
                            className="bg-primary text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-primary/90 transition-colors flex-shrink-0"
                          >
                            <i className="fa-solid fa-download"></i>
                            <span className="text-sm font-medium">Download</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <i className="fa-solid fa-file-circle-question text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500 text-sm">Tidak ada dokumen tersedia</p>
                  </div>
                )}
              </div>
            </div>

            {/* Detail Dokumen Section */}
            {dokumenFiles.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden mt-4">
                <div className="bg-accent p-3">
                  <h4 className="text-primary font-bold text-center text-white">Detail Dokumen</h4>
                </div>
                <div className="p-4">
                  {loadingDokumen ? (
                    <div className="space-y-3 animate-pulse">
                      <div className="bg-gray-200 h-4 w-full rounded"></div>
                      <div className="bg-gray-200 h-4 w-full rounded"></div>
                      <div className="bg-gray-200 h-4 w-full rounded"></div>
                      <div className="bg-gray-200 h-4 w-full rounded"></div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Nama Dokumen</span>
                        <span className="text-primary font-semibold text-sm text-right">
                          {dokumenDetail?.deskripsi || 
                           dokumenFiles[0]?.filename?.replace(/\.[^/.]+$/, '')?.replace(/_/g, ' ') ||
                           'Dokumen Investasi'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Nama Customer</span>
                        <span className="text-primary font-semibold text-sm">
                          {userData?.full_name || userData?.name || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 text-sm">Kategori</span>
                        <span className="text-primary font-semibold text-sm">Investasi</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 text-sm">Tanggal Dokumen</span>
                        <span className="text-primary font-semibold text-sm">
                          {formatDate(dokumenFiles[0]?.created_at || 
                                     dokumenFiles[0]?.modified_at || 
                                     dokumenDetail?.created_at || 
                                     dokumenDetail?.tanggal)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Deskripsi Section */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mt-4">
              <div className="bg-accent p-3">
                <h4 className="text-primary font-bold text-center text-white">Deskripsi</h4>
              </div>
              <div className="p-4">
                <p className="text-gray-500 text-sm text-center">-</p>
              </div>
            </div>

            {/* Data Keuangan Investasi Section */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mt-4">
              <div className="bg-primary p-3">
                <h4 className="text-white font-bold text-center">Data Keuangan Investasi</h4>
              </div>
              <div className="p-4">
                {/* Table Header */}
                <div className="bg-gray-50 rounded-xl p-3 mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xs font-semibold text-gray-600">Total Sapi Diinvestasikan</p>
                      <p className="text-lg font-bold text-primary">{summaryData.totalCows}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-semibold text-gray-600">Total Nilai Investasi</p>
                      <p className="text-lg font-bold text-primary">{summaryData.totalInvestasi || investmentData.investmentAmount}</p>
                    </div>
                  </div>
                </div>

                {/* Table Data Sapi */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-secondary text-white">
                        <th className="text-left py-3 px-4 text-sm font-semibold rounded-l-lg">Kode Sapi</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold rounded-r-lg">Harga Sapi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {cowsData.map((cow, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-3">
                              <div className="bg-accent p-2 rounded-lg">
                                <i className="fa-solid fa-cow text-primary text-sm"></i>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-800">{cow.id}</p>
                                <p className="text-xs text-gray-500">{cow.breed}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <p className="font-bold text-primary">{cow.purchasePrice}</p>
                            <p className="text-xs text-gray-500">Per Ekor</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Total Summary */}
                <div className="mt-4 bg-primary rounded-xl p-4">
                  <div className="flex justify-between items-center text-white">
                    <span className="font-semibold">Total Investasi Sapi</span>
                    <span className="font-bold text-lg">{summaryData.totalInvestasi || investmentData.investmentAmount}</span>
                  </div>
                  <div className="flex justify-between items-center text-white mt-2 text-sm opacity-90">
                    <span>Proyeksi Keuntungan (30%)</span>
                    <span className="font-semibold">Rp 0</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button className="bg-secondary text-white py-3 px-4 rounded-xl font-semibold text-sm hover:bg-primary transition-colors">
                    <i className="fa-solid fa-download mr-2"></i>
                    Unduh Laporan
                  </button>
                  <button className="bg-accent text-primary py-3 px-4 rounded-xl font-semibold text-sm hover:bg-accent/90 transition-colors">
                    <i className="fa-solid fa-chart-pie mr-2"></i>
                    Analisis Detail
                  </button>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Transfer Modal */}
        {showTransferModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
              {/* Modal Header */}
              <div className="bg-primary p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-accent p-2 rounded-lg">
                    <i className="fa-solid fa-receipt text-primary text-lg"></i>
                  </div>
                  <h3 className="text-white font-bold text-lg">Bukti Transfer</h3>
                </div>
                <button 
                  onClick={() => setShowTransferModal(false)}
                  className="text-white hover:text-accent transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4">
                {loadingPembayaran ? (
                  <div className="space-y-4 animate-pulse">
                    <div className="bg-gray-100 rounded-xl p-4 mb-4 text-center">
                      <div className="bg-gray-200 rounded-lg p-8 h-48"></div>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-gray-200 h-10 rounded-lg"></div>
                      <div className="bg-gray-200 h-6 rounded"></div>
                      <div className="bg-gray-200 h-6 rounded"></div>
                      <div className="bg-gray-200 h-6 rounded"></div>
                    </div>
                  </div>
                ) : pembayaranData ? (
                  <>
                    {/* Transfer Proof Image */}
                    <div className="bg-gray-100 rounded-xl p-4 mb-4 text-center">
                      {pembayaranImages.length > 0 ? (
                        <div className="bg-white rounded-lg overflow-hidden">
                          <img 
                            src={getImageUrl(pembayaranImages[0].url || pembayaranImages[0])}
                            alt="Bukti Transfer"
                            className="w-full h-auto max-h-64 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div className="hidden p-8 border-2 border-dashed border-gray-300">
                            <i className="fa-solid fa-image text-gray-400 text-4xl mb-3"></i>
                            <p className="text-gray-500 text-sm">Gambar tidak dapat dimuat</p>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-white rounded-lg p-8 border-2 border-dashed border-gray-300">
                          <i className="fa-solid fa-image text-gray-400 text-4xl mb-3"></i>
                          <p className="text-gray-500 text-sm">Gambar Bukti Transfer</p>
                          <p className="text-gray-400 text-xs mt-1">
                            {pembayaranData.bank_name || pembayaranData.bank || 'Transfer'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Transfer Details */}
                    <div className="space-y-3 mb-4">
                      <div className={`rounded-lg p-3 ${pembayaranData.status === 'approved' || pembayaranData.status === 'verified' ? 'bg-green-50' : pembayaranData.status === 'pending' ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                        <div className="flex items-center space-x-2">
                          <div className={`rounded-full w-6 h-6 flex items-center justify-center ${pembayaranData.status === 'approved' || pembayaranData.status === 'verified' ? 'bg-green-500' : pembayaranData.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-500'}`}>
                            <i className={`fa-solid ${pembayaranData.status === 'approved' || pembayaranData.status === 'verified' ? 'fa-check' : 'fa-clock'} text-white text-xs`}></i>
                          </div>
                          <span className={`font-semibold text-sm ${pembayaranData.status === 'approved' || pembayaranData.status === 'verified' ? 'text-green-700' : pembayaranData.status === 'pending' ? 'text-yellow-700' : 'text-gray-700'}`}>
                            {pembayaranData.status === 'approved' || pembayaranData.status === 'verified' 
                              ? 'Transfer Berhasil' 
                              : pembayaranData.status === 'pending' 
                              ? 'Menunggu Verifikasi' 
                              : pembayaranData.status || 'Transfer'}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs">Dari Rekening</p>
                          <p className="text-primary font-semibold">
                            {pembayaranData.rek_no || pembayaranData.from_account || pembayaranData.account_from || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Ke Rekening</p>
                          <p className="text-primary font-semibold text-xs">
                            {pembayaranData.to_account || pembayaranData.account_to || investmentData.bank || '-'}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="text-gray-500 text-xs">Jumlah Transfer</p>
                        <p className="text-primary font-bold text-lg">
                          {formatCurrency(pembayaranData.nominal || pembayaranData.amount || pembayaranData.jumlah || 0)}
                        </p>
                      </div>

                      {(() => {
                        const dateTime = formatDateTime(pembayaranData.created_at || pembayaranData.tanggal || pembayaranData.date);
                        return (
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-gray-500 text-xs">Tanggal</p>
                              <p className="text-primary font-semibold">{dateTime.date}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs">Waktu</p>
                              <p className="text-primary font-semibold">{dateTime.time}</p>
                            </div>
                          </div>
                        );
                      })()}

                      {pembayaranData.deskripsi && (
                        <div>
                          <p className="text-gray-500 text-xs">Deskripsi</p>
                          <p className="text-primary font-semibold text-sm">{pembayaranData.deskripsi}</p>
                        </div>
                      )}

                      {pembayaranData.bank_name && (
                        <div>
                          <p className="text-gray-500 text-xs">Bank</p>
                          <p className="text-primary font-semibold text-sm">{pembayaranData.bank_name}</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <i className="fa-solid fa-exclamation-circle text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500 text-sm">Data pembayaran tidak ditemukan</p>
                  </div>
                )}

                {/* Action Buttons */}
                {pembayaranData && (
                  <div className="flex space-x-3">
                    <button 
                      onClick={() => {
                        if (navigator.share && pembayaranImages.length > 0) {
                          navigator.share({
                            title: 'Bukti Transfer',
                            text: `Bukti Transfer ${pembayaranData.nominal || ''}`,
                            url: getImageUrl(pembayaranImages[0].url || pembayaranImages[0])
                          }).catch(err => console.log('Error sharing:', err));
                        } else {
                          // Fallback: copy to clipboard
                          const text = `Bukti Transfer\nJumlah: ${formatCurrency(pembayaranData.nominal || 0)}\nTanggal: ${formatDateTime(pembayaranData.created_at).date}`;
                          navigator.clipboard.writeText(text).then(() => {
                            alert('Informasi pembayaran disalin ke clipboard');
                          });
                        }
                      }}
                      className="flex-1 bg-gray-100 text-gray-600 py-3 px-4 rounded-xl flex items-center justify-center space-x-2 hover:bg-gray-200 transition-colors"
                    >
                      <i className="fa-solid fa-share"></i>
                      <span className="text-sm font-medium">Share</span>
                    </button>
                    <button 
                      onClick={() => {
                        if (pembayaranImages.length > 0) {
                          const imageUrl = getImageUrl(pembayaranImages[0].url || pembayaranImages[0]);
                          const link = document.createElement('a');
                          link.href = imageUrl;
                          const pembayaranId = pembayaranData.id || pembayaranData.pembayaran_id || 'transfer';
                          link.download = `bukti-transfer-${pembayaranId}.jpg`;
                          link.target = '_blank';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        } else {
                          alert('Tidak ada gambar untuk didownload');
                        }
                      }}
                      className="flex-1 bg-gray-100 text-gray-600 py-3 px-4 rounded-xl flex items-center justify-center space-x-2 hover:bg-gray-200 transition-colors"
                    >
                      <i className="fa-solid fa-download"></i>
                      <span className="text-sm font-medium">Download</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Navigation */}
        <footer className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-white pt-2">
          <div className="bg-primary mx-4 mb-4 rounded-2xl shadow-lg">
            <div className="flex justify-around items-center h-20 text-white">
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'home' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('home');
                  onBack();
                }}
              >
                <i className="fa-solid fa-house text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Beranda</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'savings' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('savings');
                  onBack();
                }}
              >
                <i className="fa-solid fa-piggy-bank text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Tabungan</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'products' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('products');
                  onBack();
                }}
              >
                <i className="fa-solid fa-box-archive text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Produk</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'investment' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('investment')}
              >
                <i className="fa-solid fa-arrow-trend-up text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Investasi</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'account' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('account');
                  onBack();
                }}
              >
                <i className="fa-solid fa-user-circle text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Akun</span>
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CowInvestmentDetail;
